/**
 * Audio Processing Types
 * Based on Android: services/audio/*
 *
 * Type definitions for audio recording and transcription services.
 */

// ============================================================================
// Recording Types
// ============================================================================

export enum RecordingFormat {
  WEBM = 'webm',
  WAV = 'wav',
  MP3 = 'mp3',
  OGG = 'ogg',
}

export enum RecordingQuality {
  LOW = 'LOW',
  MEDIUM = 'MEDIUM',
  HIGH = 'HIGH',
  VERY_HIGH = 'VERY_HIGH',
}

export const RecordingQualitySettings: Record<
  RecordingQuality,
  { sampleRate: number; bitRate: number }
> = {
  [RecordingQuality.LOW]: { sampleRate: 22050, bitRate: 64000 },
  [RecordingQuality.MEDIUM]: { sampleRate: 44100, bitRate: 128000 },
  [RecordingQuality.HIGH]: { sampleRate: 44100, bitRate: 256000 },
  [RecordingQuality.VERY_HIGH]: { sampleRate: 48000, bitRate: 320000 },
}

export interface RecordingMetadata {
  id: string
  filename: string
  durationMs: number
  fileSize: number
  format: RecordingFormat
  quality: RecordingQuality
  createdAt: number
  transcription?: string
  tags: string[]
  blob?: Blob
}

export type RecordingError =
  | { type: 'PERMISSION_DENIED'; message: string }
  | { type: 'NOT_SUPPORTED'; message: string }
  | { type: 'RECORDING_FAILED'; message: string }
  | { type: 'FILE_NOT_FOUND'; message: string }

export const RecordingErrors = {
  permissionDenied: (): RecordingError => ({
    type: 'PERMISSION_DENIED',
    message: 'Microphone permission denied',
  }),
  notSupported: (): RecordingError => ({
    type: 'NOT_SUPPORTED',
    message: 'Audio recording not supported in this browser',
  }),
  recordingFailed: (reason: string): RecordingError => ({
    type: 'RECORDING_FAILED',
    message: `Recording failed: ${reason}`,
  }),
  fileNotFound: (): RecordingError => ({
    type: 'FILE_NOT_FOUND',
    message: 'Recording file not found',
  }),
}

// ============================================================================
// Transcription Types
// ============================================================================

export interface TranscriptionResult {
  text: string
  language?: string
  confidence?: number
  segments?: TranscriptionSegment[]
}

export interface TranscriptionSegment {
  text: string
  start: number
  end: number
  confidence?: number
}

export interface TranscriptionOptions {
  language?: string
  prompt?: string
  temperature?: number
  timestampGranularity?: 'segment' | 'word'
}

// ============================================================================
// Quota Types
// ============================================================================

export interface RequestRecord {
  timestamp: string
  success: boolean
  statusCode?: number
  errorMessage?: string
}

export interface UsageStats {
  requestsThisMinute: number
  requestsThisHour: number
  requestsToday: number
  minuteLimit: number
  hourLimit: number
  dayLimit: number
  isRateLimited: boolean
  rateLimitResetTime?: Date
  successRate: number
  totalRequests: number
  failedRequests: number
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Format duration in milliseconds to MM:SS.cc format
 */
export function formatDuration(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000)
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  const centiseconds = Math.floor((ms % 1000) / 10)
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}.${centiseconds.toString().padStart(2, '0')}`
}

/**
 * Format duration in milliseconds to M:SS format
 */
export function formatDurationShort(ms: number): string {
  const seconds = Math.floor(ms / 1000)
  const minutes = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${minutes}:${secs.toString().padStart(2, '0')}`
}

/**
 * Format file size in bytes to human readable format
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

/**
 * Get supported audio MIME types for recording
 */
export function getSupportedMimeTypes(): string[] {
  const types = [
    'audio/webm',
    'audio/webm;codecs=opus',
    'audio/ogg;codecs=opus',
    'audio/mp4',
    'audio/wav',
  ]

  if (typeof MediaRecorder === 'undefined') {
    return []
  }

  return types.filter((type) => MediaRecorder.isTypeSupported(type))
}

/**
 * Get the best supported MIME type for recording
 */
export function getBestMimeType(): string | null {
  const supported = getSupportedMimeTypes()
  return supported[0] || null
}
